import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import DashboardLayout from "./components/DashboardLayout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import StaffDashboardPage from "./pages/StaffDashboardPage";
import EmployeesPage from "./pages/EmployeesPage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import PositionsPage from "./pages/PositionsPage";
import SitesPage from "./pages/SitesPage";
import KontrakPage from "./pages/KontrakPage";
import UsersPage from "./pages/UsersPage";
import ProfilePage from "./pages/ProfilePage";
import AttendancePage from "./pages/AttendancePage";
import AttendanceHistoryPage from "./pages/AttendanceHistoryPage";
import FaceRegistrationPage from "./pages/FaceRegistrationPage";
import LaporanKerjaPage from "./pages/LaporanKerjaPage";
import PatroliPage from "./pages/PatroliPage";
import SecurityPostsPage from "./pages/SecurityPostsPage";
import LeaveRequestPage from "./pages/LeaveRequestPage";
import ApprovalPage from "./pages/ApprovalPage";
import OvertimePage from "./pages/OvertimePage";
import ShiftManagementPage from "./pages/ShiftManagementPage";
import CalendarPage from "./pages/CalendarPage";
import PayrollPage from "./pages/PayrollPage";
import PerformancePage from "./pages/PerformancePage";
import RecruitmentPage from "./pages/RecruitmentPage";
import DocumentsPage from "./pages/DocumentsPage";
import ForceChangePasswordPage from "./pages/ForceChangePasswordPage";
import PanduanPage from "./pages/PanduanPage";
import PublicApplyPage from "./pages/PublicApplyPage";
import { PasswordChangeOfferModal } from "./components/PasswordChangeOfferModal";
import "./App.css";

// Protected Route wrapper - offers (does not force) password change for temp passwords
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      {children}
      <PasswordChangeOfferModal />
    </DashboardLayout>
  );
};

// Public Route wrapper (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Change Password Route - accessible when logged in and still on a temp password
const ForceChangePasswordRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.must_change_password) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Router - staff get a personal simplified dashboard, HR/admin get company-wide stats
const DashboardRouter = () => {
  const { isStaff } = useAuth();
  return isStaff ? <StaffDashboardPage /> : <DashboardPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Force Change Password Route */}
      <Route
        path="/change-password"
        element={
          <ForceChangePasswordRoute>
            <ForceChangePasswordPage />
          </ForceChangePasswordRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            <AttendancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/history"
        element={
          <ProtectedRoute>
            <AttendanceHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/face-registration"
        element={
          <ProtectedRoute>
            <FaceRegistrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/work-reports"
        element={
          <ProtectedRoute>
            <LaporanKerjaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patrol"
        element={
          <ProtectedRoute>
            <PatroliPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security-posts"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <SecurityPostsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave"
        element={
          <ProtectedRoute>
            <LeaveRequestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/overtime"
        element={
          <ProtectedRoute>
            <OvertimePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr', 'manager']}>
            <ApprovalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shifts"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <ShiftManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payroll"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <PayrollPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr', 'manager']}>
            <PerformancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recruitment"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <RecruitmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr', 'manager']}>
            <EmployeesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr', 'manager']}>
            <EmployeeDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <DepartmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/positions"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <PositionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <SitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kontrak"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <KontrakPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'hr']}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Panduan - always accessible, no login required */}
      <Route path="/panduan" element={<PanduanPage />} />

      {/* Public applicant biodata form - no login required */}
      <Route path="/apply" element={<PublicApplyPage />} />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
